import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import { MenuItem } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/store/store';
import { useGetThreadQuery, usePostCommentMutation } from 'src/services/threads/threads.service';
import { ThreadType, CommentType, ICommentData, CommentAccount, CommentActivityType } from 'src/services/threads/threads.types';

import FormProvider, { RHFSelect, RHFTextField, RHFMentionTextField } from 'src/components/hook-form';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

type Props = {
  thread_id?: string;
  disabled?: boolean;
};

export default function PostCommentForm({ thread_id, disabled }: Props) {

  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const tenantType = useOrgTenant();

  const [postComment] = usePostCommentMutation();

  const users = useAppSelector((state) => state?.data?.users);

  const taggedAccountsRef = useRef<CommentAccount[]>([]);

  const { currentData: thread, isLoading: isLoadingThread } = useGetThreadQuery({
    thread_id: thread_id as string
  }, {
    skip: !thread_id
  });

  const CommentSchema = Yup.object().shape({
    text: Yup.string().required('Comment is required'),
    activity_type: Yup.mixed<CommentActivityType>().optional(),
  });

  const defaultValues = {
    text: '',
    activity_type: CommentActivityType.GENERAL
  };

  const methods = useForm({
    resolver: yupResolver(CommentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {

      if (!thread) return;

      const commentData: ICommentData = {
        tagged_accounts: taggedAccountsRef.current,
      };

      let type: CommentType = CommentType.TEXT;

      switch (thread.type) {
        case ThreadType.CHAT:
          commentData.text = data.text;
          type = CommentType.TEXT;
          break;
        case ThreadType.NOTES:
          commentData.activity = {
            type: data.activity_type as CommentActivityType,
            text: data.text,
          };
          type = CommentType.ACTIVITY;
          break;
        default:
          commentData.text = data.text;
          type = CommentType.TEXT;
          break;
      }

      await postComment({
        thread_id: thread_id as string,
        type,
        data: commentData
      }).unwrap();

      reset();
    } catch (error) {
      console.error(error);

      enqueueSnackbar(t('threads.api.post.default_error'), { variant: 'error' });
    }
  });

  const buttonText = useMemo(() => {
    if (thread?.type === ThreadType.CHAT) {
      return t('threads.form.submit_comment');
    }

    if (thread?.type === ThreadType.NOTES) {
      return t('threads.form.submit_note');
    }

    return t('threads.form.submit_comment');
  }, [thread, t]);

  const activityTypeOptions = useMemo(() => Object.values(CommentActivityType).map((type) => ({
    label: t(`threads.form.activity_type.options.${type}`),
    value: type,
  })), [t]);

  // JT1-1561: chat only. In a NOTES thread Enter has to insert a newline - people type multi-line
  // notes and hit Enter expecting one, and the posted note could not be edited or deleted by a
  // non-admin author. Notes are saved from the 'Add Note' button alone.
  const onChatKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }

  const handleMentionChange = (accounts: CommentAccount[]) => {
    taggedAccountsRef.current = accounts || [];
  }

  const userOptions = useMemo(() => users?.map((user) => ({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
  })) || [], [users]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2}>

        {
          thread?.type === ThreadType.NOTES && (
            <Stack direction="row" justifyContent="flex-end" gap={1}>

              <RHFSelect
                size='small'
                name="activity_type"
                label={t('threads.form.activity_type.label')}
                sx={{ flexGrow: 1 }}
                placeholder={t('threads.form.activity_type.placeholder')}
              >
                {activityTypeOptions.map((activityType) => (
                  <MenuItem key={activityType.value} value={activityType.value}>
                    {activityType.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ flexShrink: 0, width: 200 }}
                disabled={!thread || !isValid || disabled || isLoadingThread}
                loading={isSubmitting}
              >
                {buttonText}
              </LoadingButton>
            </Stack>
          )
        }

        {
          thread?.type === ThreadType.CHAT && (
            <RHFTextField
              name="text"
              placeholder={t('threads.form.comment.placeholder')}
              multiline
              onKeyDown={onChatKeyDown}
              disabled={disabled}
              rows={4}
            />
          )
        }

        {
          thread?.type === ThreadType.NOTES && (
            <>
              {

                tenantType === TenantType.Candidate ? (
                  <RHFTextField
                    name="text"
                    placeholder={t('threads.form.comment.placeholder')}
                    multiline
                    disabled={disabled}
                    rows={4}
                  />
                ) : (
                  <RHFMentionTextField
                    name="text"
                    placeholder={t('threads.form.comment.placeholder')}
                    multiline
                    disabled={disabled}
                    rows={4}
                    onMentionChange={handleMentionChange}
                    users={userOptions as any[]}
                  />
                )
              }
            </>
          )
        }

        {
          thread?.type === ThreadType.CHAT && (
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ flexShrink: 0, width: 200 }}
                disabled={!thread || !isValid || disabled || isLoadingThread}
                loading={isSubmitting}
              >
                {buttonText}
              </LoadingButton>
            </Stack>
          )
        }

      </Stack>
    </FormProvider>
  );
}
